import { useSearchParams } from "@solidjs/router";
import { createForm } from "@tanstack/solid-form";
import { FaSolidArrowDownAZ, FaSolidArrowUpAZ } from "solid-icons/fa";
import { IoEyeOff, IoSearch } from "solid-icons/io";
import {
  type Component,
  createDeferred,
  createEffect,
  createMemo,
  createSignal,
  For,
  Show,
  splitProps,
} from "solid-js";
import { produce } from "solid-js/store";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cleanObject } from "@/libs/global";
import {
  ALLOWED_FILTERS,
  ALLOWED_SORT_CONFIG,
  ALLOWED_SORT_FIELDS,
} from "../constants/filters";
import { useFilterBadges, useFilterStore } from "../context/FilterProvider";
import { Effort, JobCategoryEnum } from "../create/constants";
import type { AllowedFilters, AllowedSortFields } from "../types";

type NudgeLabel =
  | "backend"
  | "frontend"
  | "design"
  | "fullTime"
  | "partTime"
  | "freelance"
  | "remote"
  | "hybrid"
  | "onSite"
  | "internship";

const NUDGE_MESSAGES = [
  {
    id: 1,
    filter: "job_category",
    label: "backend" as NudgeLabel,
    value: JobCategoryEnum.WEB_MOBILE_SOFTWARE_DEV,
  },
  {
    id: 2,
    filter: "job_category",
    label: "frontend" as NudgeLabel,
    value: JobCategoryEnum.WEB_MOBILE_SOFTWARE_DEV,
  },
  {
    id: 3,
    filter: "job_category",
    label: "design" as NudgeLabel,
    value: JobCategoryEnum.DESIGN_CREATIVE,
  },
  {
    id: 4,
    filter: "effort",
    label: "20-30" as NudgeLabel,
    value: Effort.TWENTY_TO_THIRTY,
  },
  {
    id: 5,
    filter: "skills",
    label: "react" as NudgeLabel,
    value: ["React", "JavaScript", "TypeScript"],
  },
  {
    id: 6,
    filter: "skills",
    label: "django" as NudgeLabel,
    value: ["Django", "Python", "PostgreSQL"],
  },
] satisfies {
  id: number;
  filter: AllowedFilters;
  label: NudgeLabel;
  value: string | string[];
}[];
const _FilterNudges: Component = () => {
  const { t } = useI18n();
  const [selectedNudge, setSelectedNudge] = createSignal<number | null>(null);
  const [filterStore, setFilterStore] = useFilterStore();
  const setNudgedFilter = (
    filter: AllowedFilters,
    value: string | string[] | null,
  ) => {
    const valueString = Array.isArray(value) ? value.join(",") : value;
    setFilterStore(
      produce((state) => {
        for (const key in state) {
          if (key === "renewable") {
            state[key] = valueString === "all" ? null : valueString === "true";
          }
          if (key === (filter as Exclude<AllowedFilters, "renewable">)) {
            state[key] = valueString;
          } else {
            if (
              ALLOWED_FILTERS.includes(
                key as Exclude<
                  AllowedFilters,
                  "sortBy" | "sortOrder" | "payment_status" | "project_type"
                >,
              )
            ) {
              state[key as AllowedFilters] = null;
            }
          }
        }
      }),
    );
  };

  const activeNudge = createMemo(() => {
    const activeNudge = NUDGE_MESSAGES.find((nudge) => {
      return nudge.id === selectedNudge();
    });
    const isActive = !filterStore.availableFilters.includes(
      activeNudge?.filter as AllowedFilters,
    );
    return { ...activeNudge, isActive };
  });

  return (
    <div class="flex items-center gap-2 flex-wrap">
      <For each={NUDGE_MESSAGES}>
        {(nudge) => (
          <button
            type="button"
            class="flex items-center gap-2"
            onClick={() => {
              if (selectedNudge() === nudge.id && activeNudge().isActive) {
                setNudgedFilter(nudge.filter as AllowedFilters, null);
                setSelectedNudge(null);
              } else {
                setNudgedFilter(nudge.filter as AllowedFilters, nudge.value);
                setSelectedNudge(nudge.id);
              }
            }}
          >
            <Badge
              variant={
                selectedNudge() === nudge.id && activeNudge().isActive
                  ? "default"
                  : "secondary"
              }
            >
              <p>{t(`home.nudges.jobType.${nudge.label}`)}</p>
              <Show
                when={selectedNudge() === nudge.id && activeNudge().isActive}
              >
                <IoEyeOff size={16} class="ml-2" />
              </Show>
            </Badge>
          </button>
        )}
      </For>
    </div>
  );
};

export const SearchBar: Component = () => {
  const [filterStore, setFilterStore] = useFilterStore();
  const [search, setSearch] = createSignal(filterStore.search);
  const deferredSearch = createDeferred(search);

  const availableParams = createMemo(() => ({
    sortBy: filterStore.sortBy ?? "created_at",
    sortOrder: filterStore.sortOrder ?? "desc",
  }));
  const availableFilters = createMemo(() =>
    filterStore?.availableFilters?.reduce(
      (acc, filter) => {
        acc[filter] = "";
        return acc;
      },
      {
        ...availableParams,
      } as Record<string, string>,
    ),
  );
  const filterBadges = useFilterBadges();

  const [, setSearchParams] = useSearchParams();

  const form = createForm(() => ({
    defaultValues: {
      job_category: filterStore.job_category,
      skills: filterStore.skills,
      experience_level: filterStore.experience_level,
      daily_rate_min: filterStore.daily_rate_min,
      daily_rate_max: filterStore.daily_rate_max,
      budget: filterStore.budget,
      duration: filterStore.duration,
      effort: filterStore.effort,
      payment_type: filterStore.payment_type,
      renewable: filterStore.renewable,
      remote_policy: filterStore.remote_policy,
      start_date: filterStore.start_date,
    } satisfies Partial<
      Record<
        Exclude<AllowedFilters, "payment_status">,
        string | null | undefined | boolean
      >
    >,
    onSubmit: (data) => {
      setFilterStore(data.value);
    },
  }));

  createEffect(() => {
    if (filterBadges().length > 0) {
      const params = filterBadges().reduce(
        (acc, param) => {
          if (param.key === "renewable") {
            acc[param.key] =
              param.value === "all" ? null : Boolean(param.value);
          } else if (param.value) {
            acc[param.key] = param.value;
            form.setFieldValue(
              param.key as Exclude<
                AllowedFilters,
                "project_type" | "payment_status" | "renewable"
              >,
              param.value as string | null | undefined,
            );
          }
          return acc;
        },
        {
          ...availableFilters(),
          ...availableParams(),
        } as Record<string, string | boolean | null>,
      );

      setSearchParams(params, {
        replace: true,
      });
    } else {
      const params = cleanObject({
        ...availableFilters(),
        ...availableParams(),
      });
      setSearchParams(params, {
        replace: true,
      });
      form.reset();
      createEffect(() => {
        if (!search()) {
          setFilterStore("search", undefined);
          setSearchParams(
            {
              ...availableFilters(),
              ...availableParams(),
            },
            {
              replace: true,
            },
          );
        }
      });
    }
  });

  const { t } = useI18n();

  let buttonRef: HTMLButtonElement | undefined;

  return (
    <section>
      <div class="bg-white rounded-md p-4 w-full relative">
        <div class="flex items-center gap-1 w-[calc(100%-112px)]">
          <IoSearch size={20} class="text-gray-400" />
          <Input
            placeholder={t("home.search.placeholder")}
            class="w-full border-none focus-visible:ring-0 focus-visible:ring-offset-0"
            value={search()}
            onInput={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                buttonRef?.click();
              }
            }}
          />
        </div>
        <button
          ref={buttonRef}
          type="button"
          class="h-full w-28 flex items-center justify-center bg-primary absolute right-0 top-0 text-white rounded-r-md  font-medium"
          classList={{
            "opacity-50 cursor-not-allowed": !search(),
            "opacity-100 hover:bg-primary/90 transition-colors cursor-pointer":
              !!search(),
          }}
          disabled={!search()}
          onClick={() => {
            setFilterStore("search", deferredSearch());
            const params = cleanObject({
              ...availableFilters(),
              ...availableParams(),
              search: deferredSearch() ?? undefined,
            });
            setSearchParams(params, {
              replace: true,
            });
          }}
        >
          {t("common.search")}
        </button>
      </div>
      {/* <div class="bg-white rounded-md p-4 w-full border border-gray-200 mt-8 space-y-2">
        <p class="text-xs text-gray-500">{t("home.search.popularFilters")}</p>
        <FilterNudges />
      </div> */}
    </section>
  );
};

export const SortSelector: Component = () => {
  const [filterStore, setFilterStore] = useFilterStore();
  const [sortRules] = splitProps(filterStore, ["sortBy", "sortOrder"]);
  const activeSort = createMemo(() => {
    return ALLOWED_SORT_CONFIG[
      sortRules.sortBy as Exclude<
        AllowedSortFields,
        "budget" | "estimated_deadline" | "project_end"
      >
    ];
  });
  return (
    <div class="items-center justify-end gap-2 w-full md:w-auto hidden md:flex">
      <Select
        value={sortRules.sortBy}
        options={ALLOWED_SORT_FIELDS}
        onChange={(value) => {
          if (!value) return;
          if (value !== activeSort()?.key) {
            setFilterStore("sortBy", value as AllowedSortFields);
          }
        }}
        itemComponent={(props) => (
          <SelectItem {...props}>
            {
              ALLOWED_SORT_CONFIG[
                props.item.rawValue as Exclude<
                  AllowedSortFields,
                  "budget" | "estimated_deadline" | "project_end"
                >
              ].label
            }
          </SelectItem>
        )}
      >
        <SelectTrigger class="md:min-w-[196px] w-full min-w-fit bg-white">
          <SelectValue class="w-full text-left text-sm">
            {" "}
            {activeSort()?.label}
          </SelectValue>
        </SelectTrigger>
        <SelectContent />
      </Select>
      <Button
        variant="outline"
        size="default"
        class="w-fit"
        onClick={() =>
          setFilterStore(
            "sortOrder",
            sortRules.sortOrder === "asc" ? "desc" : "asc",
          )
        }
      >
        <Show
          when={sortRules.sortOrder === "asc"}
          fallback={<FaSolidArrowDownAZ />}
        >
          <FaSolidArrowUpAZ />
        </Show>
      </Button>
    </div>
  );
};
